import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// Constants
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TIMEOUT = 30000; // 30 seconds

// Error types
export interface ApiError {
  status: number;
  message: string;
  errors?: any;
}

// Generic API response type
export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

/**
 * Utility to normalize API responses to a consistent format
 * Handles both formats:
 * 1. Direct: { property1, property2, ... }
 * 2. Nested: { statusCode, message, data: { property1, property2, ... } }
 * 
 * @param response The API response to normalize
 * @returns A normalized response with data always accessible at the top level
 */
export function normalizeApiResponse<T>(response: any): T {
  if (!response) {
    return response;
  }
  
  // If response has the nested structure with statusCode, message, and data
  if (response && typeof response === 'object' && 'statusCode' in response && response.data) {
    // Create a normalized response combining properties from both levels
    return {
      ...response.data, // Primary data at top level 
      _response: { // Keep original metadata nested
        statusCode: response.statusCode,
        message: response.message
      }
    } as unknown as T;
  }
  
  // If already in the expected format, return as is
  return response as T;
}

// Base API client class
class ApiClient {
  private static instance: ApiClient;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Use our normalization utility for consistent response handling
        return normalizeApiResponse(response.data);
      },
      async (error) => {
        // Format API errors
        const apiError: ApiError = {
          status: error.response?.status || 500,
          message: error.response?.data?.message || 'An unexpected error occurred',
          errors: error.response?.data?.errors,
        };

        return Promise.reject(apiError);
      }
    );
  }

  // Singleton pattern
  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  // HTTP methods with simplified interface (no auth needed)
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`API Client: Making GET request to: ${url}`);
      const response = await this.axiosInstance.get<T>(url, config);
      const responseData = response as unknown as T;
      console.log(`API Client: GET response for ${url}:`, responseData);
      
      // Add detailed structure logging for debugging
      if (responseData) {
        console.log(`API Client: Response structure details for ${url}:`);
        console.log(`- Type: ${typeof responseData}`);
        console.log(`- Keys: ${Object.keys(responseData as object).join(', ')}`);
        
        // Type assertion to access potential _response property
        const typedResponse = responseData as any;
        if (typedResponse._response) {
          console.log(`- _response: ${JSON.stringify(typedResponse._response)}`);
        }
        
        // Log the first few entries if it's an array-like object
        const numericKeys = Object.keys(responseData as object).filter(key => !isNaN(Number(key)));
        if (numericKeys.length > 0 && numericKeys.includes('0')) {
          console.log(`- First item sample: ${JSON.stringify(typedResponse[0])}`);
        }
      }
      
      return responseData;
    } catch (error) {
      console.error(`API Client: Error in GET request to ${url}:`, error);
      throw error;
    }
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response as unknown as T;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      console.log(`API Client: Making PUT request to: ${url}`);
      const response = await this.axiosInstance.put<T>(url, data, config);
      const responseData = response as unknown as T;
      console.log(`API Client: PUT response for ${url}:`, responseData);
      
      return responseData;
    } catch (error) {
      console.error(`API Client: Error in PUT request to ${url}:`, error);
      throw error;
    }
  }
}

// Export the singleton instance
const apiClient = ApiClient.getInstance();
export default apiClient; 